import { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Wallet, ChevronDown, ChevronRight, CheckCircle2, AlertCircle, Download, FileText, Link2, X, Check } from "lucide-react";
import { useAuth } from "../lib/auth";

const SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
const fmt = (v, c) => `${SYM[c] || c}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Founder-only aggregate — every partner, expandable to show which
 * participant / booking is paid vs due. Data comes from
 * `/api/partner-payments/overview` which joins sales, weekend_sales and
 * partner_payments per booking.
 */
export default function PartnerPaymentsOverview() {
  const { user } = useAuth();
  const isFounder = (user?.real_role || user?.effective_role) === "FOUNDER";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  // Attribute-in-place: when set, opens the booking-picker modal so the
  // Founder can tag an unallocated payment to a booking without leaving
  // the Revenue Ledger. Shape: { partner, payment }.
  const [attributing, setAttributing] = useState(null);
  // Mark-as-paid modal state — replaces window.prompt (which was silently
  // suppressed by the browser in some contexts, making the button appear
  // dead). Shape: { row } where row is the participant/booking record.
  const [markPaying, setMarkPaying] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get("/partner-payments/overview")
      .then(({ data }) => setRows(data?.partners || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const toggle = (id) => setExpanded(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Precompute per-partner metadata (participants list, sorted
  // currency entries, "fully paid" flag). Was previously inline in
  // the row render — `Object.entries().sort()` on every re-render
  // was flagged by the perf audit. Now runs once per `rows` change.
  const partnersWithMeta = useMemo(() => rows.map(p => {
    const participants = p.participants || [];
    const totals = p.currency_totals || {};
    const currencyEntries = Object.entries(totals).sort(([a], [b]) => a.localeCompare(b));
    const owes = Object.values(totals).some(t => (t.balance || 0) > 0.5);
    return { ...p, _participants: participants, _currencyEntries: currencyEntries, _fully: !owes };
  }), [rows]);

  const exportCSV = () => {
    // Flatten every partner → participant row into a single CSV so the
    // Founder can drop it into Sheets / Xero for reconciliation. Unallocated
    // payments surface as pseudo-rows with a "— UNALLOCATED —" participant.
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    // Founders see the raw partner email; every other role gets it redacted
    // to stay consistent with the strict data-redaction guardrails applied
    // across the UI and JSON API responses.
    const header = ["Partner", ...(isFounder ? ["Email"] : []), "Participant", "Kind", "Programme", "Destination", "City", "Start", "Currency", "Invoiced", "Paid", "Paid (other currencies)", "Balance"];
    const lines = [header.join(",")];
    for (const p of rows) {
      for (const r of (p.participants || [])) {
        const paidOther = r.paid_other ? Object.entries(r.paid_other).map(([c, v]) => `${c} ${v}`).join(" | ") : "";
        lines.push([p.name, ...(isFounder ? [p.email] : []), r.customer, r.kind, r.programme, r.destination, r.city, r.start_date, r.currency, r.invoiced, r.paid, paidOther, r.balance].map(esc).join(","));
      }
      for (const [c, v] of Object.entries(p.unallocated || {})) {
        lines.push([p.name, ...(isFounder ? [p.email] : []), "— UNALLOCATED —", "payment", "", "", "", "", c, "", v, "", ""].map(esc).join(","));
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Revenue Ledger CSV downloaded");
  };

  const downloadReceipt = async (bookingId) => {
    try {
      const res = await api.get(`/partner-portal/receipt/${bookingId}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-${bookingId}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download receipt");
    }
  };

  if (loading) return <div className="p-6 text-sm text-[color:var(--aii-muted)]">Loading…</div>;

  const grandInvoiced = {}, grandPaid = {}, grandBalance = {};
  for (const r of rows) {
    for (const [c, t] of Object.entries(r.currency_totals || {})) {
      grandInvoiced[c] = (grandInvoiced[c] || 0) + (t.invoiced || 0);
      grandPaid[c] = (grandPaid[c] || 0) + (t.paid || 0);
      grandBalance[c] = (grandBalance[c] || 0) + (t.balance || 0);
    }
  }

  return (
    <div className="space-y-4" data-testid="partner-payments-overview">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Wallet size={11}/> Revenue Ledger</div>
          <h1 className="text-2xl font-display font-semibold mt-0.5">Who paid, who owes</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1 max-w-2xl">
            Every partner account with per-currency totals — click a partner to drill down to which participants are paid, which still owe, and by how much across program + weekend bookings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isFounder && rows.some(p => (p.participants || []).some(r => r.balance < -0.5)) && (
            <button
              data-testid="ppo-heal-negatives"
              onClick={async () => {
                if (!window.confirm("Heal all negative balances?\n\nThis will delete any duplicate 'Founder mark-paid' rows that pushed an invoice below zero.  Real partner payments are never touched.")) return;
                try {
                  const { data } = await api.post("/partner-payments/heal-negative-balances");
                  toast.success(`Healed ${data.count} row${data.count === 1 ? "" : "s"}`);
                  reload();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Heal failed");
                }
              }}
              title="Trim duplicate Mark-as-Paid entries so no invoice sits at a negative balance"
              className="text-xs px-3 py-2 border border-amber-500 text-amber-700 rounded-md hover:bg-amber-50 font-semibold flex items-center gap-1.5">
              Heal negatives
            </button>
          )}
          <button
            data-testid="ppo-export-csv"
            onClick={exportCSV}
            disabled={rows.length === 0}
            className="text-xs px-3 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5 disabled:opacity-50">
            <Download size={12}/> Export CSV
          </button>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-2" data-testid="ppo-totals">
          <div className="aii-card p-4 border-l-4 border-sky-400">
            <div className="text-[10px] uppercase tracking-widest text-sky-800 font-bold">Invoiced (all partners)</div>
            <div className="text-xl font-display font-semibold mt-1 text-sky-900">{Object.entries(grandInvoiced).map(([c, v]) => fmt(v, c)).join(" · ") || "—"}</div>
          </div>
          <div className="aii-card p-4 border-l-4 border-emerald-500">
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold">Paid</div>
            <div className="text-xl font-display font-semibold mt-1 text-emerald-900">{Object.entries(grandPaid).map(([c, v]) => fmt(v, c)).join(" · ") || "—"}</div>
          </div>
          <div className="aii-card p-4 border-l-4 border-amber-500">
            <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold">Outstanding</div>
            <div className="text-xl font-display font-semibold mt-1 text-amber-900">{Object.entries(grandBalance).filter(([, v]) => v > 0.5).map(([c, v]) => fmt(v, c)).join(" · ") || "—"}</div>
          </div>
        </div>
      )}

      {rows.length === 0 && (
        <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)] italic">No partner has an invoice or a logged payment yet.</div>
      )}

      <div className="space-y-2">
        {partnersWithMeta.map(p => {
          const participants = p._participants;
          const currencyEntries = p._currencyEntries;
          const fully = p._fully;
          return (
            <div key={p.id} className={`aii-card overflow-hidden border-l-4 ${fully ? "border-emerald-500" : "border-amber-500"}`} data-testid={`ppo-partner-${p.id}`}>
              <button onClick={() => toggle(p.id)}
                className="w-full flex items-center justify-between gap-3 p-4 hover:bg-slate-50 transition text-left">
                <div className="flex items-center gap-2 min-w-0">
                  {expanded.has(p.id) ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{p.name}</div>
                    <div className="text-[11px] text-[color:var(--aii-muted)] truncate">{p.email} · {participants.length} participant{participants.length === 1 ? "" : "s"}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {fully ? (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 flex items-center gap-1"><CheckCircle2 size={9}/> Settled</span>
                  ) : (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800 flex items-center gap-1"><AlertCircle size={9}/> Owes</span>
                  )}
                  {currencyEntries.map(([c, t]) => (
                    <div key={c} className="text-[11px] px-2 py-1 rounded border border-[color:var(--aii-border)] bg-white flex items-center gap-1.5" data-testid={`ppo-partner-total-${p.id}-${c}`}>
                      <span className="text-emerald-700 font-semibold" title={`Total received from partner in ${c}`}>{fmt(t.paid, c)} paid</span>
                      <span className="text-[color:var(--aii-muted)]">·</span>
                      <span className={t.balance > 0.5 ? "text-amber-800 font-semibold" : "text-emerald-700 font-semibold"} title={`Outstanding balance in ${c}`}>{fmt(t.balance, c)} due</span>
                      <span className="text-[color:var(--aii-muted)] text-[10px]">of {fmt(t.invoiced, c)}</span>
                    </div>
                  ))}
                </div>
              </button>
              {expanded.has(p.id) && (
                <div className="border-t border-[color:var(--aii-border)] p-3 bg-slate-50/50">
                  {(p.unallocated_payments || []).length > 0 && (
                    <div className="mb-3 p-3 rounded-md bg-amber-50 border border-amber-300" data-testid={`ppo-unallocated-list-${p.id}`}>
                      <div className="flex items-start gap-2">
                        <AlertCircle size={16} className="text-amber-700 mt-0.5 shrink-0"/>
                        <div className="flex-1">
                          <div className="font-semibold text-amber-900 text-sm">Verified · not tagged to any booking yet</div>
                          <div className="text-[11px] text-amber-800 mt-0.5">Click <b>Attribute</b> on any row to snap it onto a participant without leaving this page.</div>
                        </div>
                      </div>
                      <div className="mt-2 space-y-1.5">
                        {p.unallocated_payments.map(u => (
                          <div key={u.id} data-testid={`ppo-unalloc-row-${u.id}`}
                               className="flex items-center gap-2 p-2 bg-white border border-amber-200 rounded text-xs">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-amber-900">{fmt(u.amount, u.currency)}
                                {u.reference && <span className="ml-2 text-[color:var(--aii-muted)] font-normal">· {u.reference}</span>}
                              </div>
                              <div className="text-[10px] text-[color:var(--aii-muted)]">
                                Paid on {fmtDate(u.paid_on)}{u.method ? ` · via ${u.method}` : ""}
                              </div>
                            </div>
                            <button
                              data-testid={`ppo-attribute-btn-${u.id}`}
                              onClick={() => setAttributing({ partner: p, payment: u })}
                              disabled={(p.participants || []).length === 0}
                              title={(p.participants || []).length === 0 ? "This partner has no bookings to attribute to" : "Assign this payment to a participant"}
                              className="text-[11px] px-2.5 py-1 bg-brand text-white rounded font-semibold hover:bg-brand-dark disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 whitespace-nowrap">
                              <Link2 size={11}/> Attribute
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {Object.keys(p.unallocated_pending || {}).length > 0 && (
                    <div className="mb-3 p-3 rounded-md bg-amber-50 border border-amber-300 flex items-start gap-2" data-testid={`ppo-unallocated-pending-${p.id}`}>
                      <AlertCircle size={16} className="text-amber-700 mt-0.5 shrink-0"/>
                      <div className="text-sm flex-1">
                        <div className="font-semibold text-amber-900">Pending approval:</div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {Object.entries(p.unallocated_pending || {}).map(([c, v]) => (
                            <span key={c} className="px-2 py-0.5 bg-white border border-amber-300 rounded text-amber-900 font-semibold text-xs">{fmt(v, c)} pending</span>
                          ))}
                        </div>
                        <div className="text-[11px] text-amber-800 mt-1.5">
                          Approve pending payments at <Link to="/pending-payments" className="underline decoration-dotted font-semibold">Pending Approvals</Link>. Only verified payments reduce the balance.
                        </div>
                      </div>
                    </div>
                  )}
                  {participants.length === 0 ? (
                    <div className="text-xs text-[color:var(--aii-muted)] italic">Only unallocated payments — no per-booking breakdown.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="aii-table" data-testid={`ppo-participants-${p.id}`}>
                        <thead><tr>
                          <th>Participant</th><th>Kind</th><th>Programme</th><th>Destination · City</th><th>Start</th>
                          <th className="text-right">Invoiced</th><th className="text-right">Paid</th><th className="text-right">Balance</th><th></th>
                        </tr></thead>
                        <tbody>
                          {participants.map(r => (
                            <tr key={`${r.booking_id}-${r.kind}`} data-testid={`ppo-row-${r.booking_id}`}
                              className={r.balance <= 0.5 ? "text-[color:var(--aii-muted)]" : ""}>
                              <td className="font-medium">{r.customer}</td>
                              <td>
                                <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.kind === "program" ? "bg-sky-100 text-sky-800" : "bg-violet-100 text-violet-800"}`}>
                                  {r.kind}
                                </span>
                              </td>
                              <td className="text-xs" data-testid={`ppo-programme-${r.booking_id}`}>{r.programme}</td>
                              <td className="text-xs whitespace-nowrap" data-testid={`ppo-destcity-${r.booking_id}`}>
                                {(r.destination || r.city) ? (
                                  <>
                                    {r.destination && <span className="font-medium">{r.destination}</span>}
                                    {r.destination && r.city && <span className="text-[color:var(--aii-muted)]"> · </span>}
                                    {r.city && <span>{r.city}</span>}
                                  </>
                                ) : (
                                  <span className="text-[color:var(--aii-muted)]">—</span>
                                )}
                              </td>
                              <td className="text-xs">{fmtDate(r.start_date)}</td>
                              <td className="text-right">{fmt(r.invoiced, r.currency)}</td>
                              <td className="text-right text-emerald-700">
                                <div>{fmt(r.paid, r.currency)}</div>
                                {/* Cross-currency payments — surfaced so
                                    the Founder sees the partner DID pay,
                                    just in a different currency to the
                                    invoice.  Balance still respects the
                                    invoice currency (we don't do FX). */}
                                {r.paid_other && Object.keys(r.paid_other).length > 0 && (
                                  <div className="text-[10px] text-[color:var(--aii-muted)] mt-0.5 font-normal" data-testid={`ppo-paidother-${r.booking_id}`}>
                                    + {Object.entries(r.paid_other).map(([c, v]) => fmt(v, c)).join(" · ")}
                                    <span className="italic ml-1">(other currency)</span>
                                  </div>
                                )}
                                {/* Pending approval — the partner has
                                    logged this money but Founder hasn't
                                    verified yet, so it does NOT reduce
                                    the balance.  Approve on the Pending
                                    Payments page to move it into `paid`. */}
                                {r.pending_paid > 0.005 && (
                                  <div className="text-[10px] text-amber-800 mt-0.5 font-semibold" data-testid={`ppo-pending-${r.booking_id}`}
                                    title="Awaiting Founder approval — not counted in balance">
                                    + {fmt(r.pending_paid, r.currency)} pending
                                  </div>
                                )}
                              </td>
                              <td className={`text-right font-semibold ${r.balance > 0.5 ? "text-amber-700" : "text-emerald-700"}`}>{fmt(r.balance, r.currency)}</td>
                              <td className="text-right">
                                {r.balance <= 0.5 && r.invoiced > 0 && (
                                  <button data-testid={`ppo-receipt-${r.booking_id}`} onClick={() => downloadReceipt(r.booking_id)}
                                    title="Download PAID-IN-FULL receipt PDF"
                                    className="text-brand hover:underline text-xs inline-flex items-center gap-1">
                                    <FileText size={11}/> Receipt
                                  </button>
                                )}
                                {isFounder && r.balance > 0.5 && r.booking_id && r.customer && r.customer !== "—" && (
                                  <button
                                    type="button"
                                    data-testid={`ppo-markpaid-${r.booking_id}`}
                                    onClick={(e) => {
                                      // Stop propagation so we don't accidentally
                                      // toggle the partner accordion row.
                                      e.stopPropagation();
                                      setMarkPaying({ row: r });
                                    }}
                                    title="Founder shortcut — records an approved payment for the full outstanding balance"
                                    className="text-emerald-700 hover:bg-emerald-50 border border-emerald-300 rounded px-2 py-1 text-xs inline-flex items-center gap-1 font-semibold cursor-pointer">
                                    <Check size={11}/> Mark as Paid
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {Object.keys(p.unallocated || {}).length > 0 && (
                    <div className="mt-2 text-[11px] text-[color:var(--aii-muted)] italic">
                      Tip: use <b>Attribute</b> above to snap an unallocated payment onto a specific booking.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {attributing && (
        <AttributeModal
          partner={attributing.partner}
          payment={attributing.payment}
          onClose={() => setAttributing(null)}
          onSaved={() => { setAttributing(null); reload(); }}
        />
      )}

      {markPaying && (
        <MarkPaidModal
          row={markPaying.row}
          onClose={() => setMarkPaying(null)}
          onSaved={() => { setMarkPaying(null); reload(); }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Booking-picker modal — attribute an unallocated verified payment
// to one of the partner's participants without leaving the Revenue
// Ledger. Balance updates on save.
// ------------------------------------------------------------
function AttributeModal({ partner, payment, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [pick, setPick] = useState(null); // {booking_id, kind}
  const [q, setQ] = useState("");

  const options = useMemo(() => {
    const list = partner.participants || [];
    const query = q.trim().toLowerCase();
    if (!query) return list;
    return list.filter(r => (r.customer || "").toLowerCase().includes(query)
      || (r.programme || "").toLowerCase().includes(query)
      || (r.destination || "").toLowerCase().includes(query)
      || (r.city || "").toLowerCase().includes(query));
  }, [partner, q]);

  const save = async () => {
    if (!pick) { toast.error("Pick a booking first"); return; }
    setSaving(true);
    try {
      await api.patch(`/partner-accounts/${partner.id}/payments/${payment.id}`, {
        booking_id: pick.booking_id,
        booking_kind: pick.kind,
      });
      toast.success(`${SYM[payment.currency] || ""}${payment.amount} attributed to ${pick.customer}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attribution failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
           onClick={e => e.stopPropagation()} data-testid="attribute-modal">
        <div className="p-5 border-b border-[color:var(--aii-border)]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Link2 size={11}/> Attribute payment</div>
              <h3 className="text-lg font-display font-semibold mt-1 truncate">
                {fmt(payment.amount, payment.currency)} · {partner.name}
              </h3>
              <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">
                {payment.reference ? <>Ref: <b>{payment.reference}</b> · </> : null}
                Paid on {fmtDate(payment.paid_on)}
              </div>
            </div>
            <button onClick={onClose} data-testid="attribute-close" className="p-1.5 hover:bg-slate-100 rounded"><X size={16}/></button>
          </div>
          <input
            data-testid="attribute-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by participant, programme, destination…"
            className="input-aii w-full mt-3 text-sm"/>
        </div>
        <div className="flex-1 overflow-y-auto p-3 bg-slate-50/50">
          {options.length === 0 ? (
            <div className="text-center text-sm text-[color:var(--aii-muted)] italic py-10">
              {(partner.participants || []).length === 0
                ? "This partner has no bookings yet — attribute becomes possible once a booking is invoiced."
                : "No bookings match your search."}
            </div>
          ) : (
            <div className="space-y-1.5">
              {options.map(r => {
                const isPicked = pick && pick.booking_id === r.booking_id && pick.kind === r.kind;
                const currencyMismatch = r.currency !== payment.currency;
                return (
                  <button
                    key={`${r.booking_id}-${r.kind}`}
                    data-testid={`attribute-option-${r.booking_id}`}
                    onClick={() => setPick({ booking_id: r.booking_id, kind: r.kind, customer: r.customer })}
                    className={`w-full text-left p-3 rounded border-2 transition ${isPicked ? "border-brand bg-[color:var(--aii-green-tint)]" : "border-transparent bg-white hover:border-[color:var(--aii-border)]"}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate flex items-center gap-2">
                          {r.customer}
                          <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${r.kind === "program" ? "bg-sky-100 text-sky-800" : "bg-violet-100 text-violet-800"}`}>{r.kind}</span>
                        </div>
                        <div className="text-[11px] text-[color:var(--aii-muted)] truncate">
                          {r.programme}{r.destination ? ` · ${r.destination}` : ""}{r.city ? ` · ${r.city}` : ""}{r.start_date ? ` · ${fmtDate(r.start_date)}` : ""}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] text-[color:var(--aii-muted)] uppercase tracking-widest font-bold">Balance</div>
                        <div className={`text-sm font-semibold ${r.balance > 0.5 ? "text-amber-700" : "text-emerald-700"}`}>{fmt(r.balance, r.currency)}</div>
                        {currencyMismatch && (
                          <div className="text-[9px] text-amber-700 uppercase font-bold mt-0.5">
                            {r.currency} ≠ {payment.currency}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-[color:var(--aii-border)] flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button
            data-testid="attribute-save"
            disabled={!pick || saving}
            onClick={save}
            className="bg-brand text-white font-semibold px-4 py-1.5 rounded-md hover:bg-brand-dark disabled:opacity-50 text-sm flex items-center gap-1.5">
            <Link2 size={13}/> {saving ? "Attributing…" : "Attribute to selected"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ------------------------------------------------------------
// Mark-as-Paid modal — Founder shortcut to zero-out an outstanding
// balance in one click.  Replaces the earlier window.prompt() which
// was silently suppressed by the browser in some embedded contexts,
// making the button appear unclickable.
// ------------------------------------------------------------
function MarkPaidModal({ row, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [reference, setReference] = useState(`Cash · ${today}`);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/partner-payments/mark-paid", {
        booking_id: row.booking_id,
        kind: row.kind,
        amount: row.balance,
        currency: row.currency,
        reference: reference.trim(),
      });
      toast.success(`Marked paid — ${fmt(row.balance, row.currency)}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark paid");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="markpaid-modal-backdrop">
      <div
        className="bg-white rounded-xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="markpaid-modal">
        <div className="p-5 border-b border-[color:var(--aii-border)]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1">
                <Check size={11}/> Mark as Paid
              </div>
              <h3 className="text-lg font-display font-semibold mt-1 truncate">
                {row.customer}
              </h3>
              <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 truncate">
                {row.programme}{row.destination ? ` · ${row.destination}` : ""}{row.city ? ` · ${row.city}` : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="markpaid-close"
              className="p-1.5 hover:bg-slate-100 rounded"><X size={16}/></button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold">Amount to record</div>
            <div className="text-2xl font-display font-semibold text-emerald-900 mt-0.5" data-testid="markpaid-amount">
              {fmt(row.balance, row.currency)}
            </div>
            <div className="text-[11px] text-emerald-800 mt-1">Records an approved payment for the full outstanding balance in {row.currency}.</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">
              Reference (bank txn ID, cheque #, memo)
            </label>
            <input
              data-testid="markpaid-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={`Cash · ${today}`}
              className="input-aii w-full mt-1 text-sm"
              autoFocus
            />
          </div>
        </div>
        <div className="p-4 border-t border-[color:var(--aii-border)] flex justify-end gap-2 bg-slate-50/50">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="markpaid-cancel"
            className="px-3 py-1.5 text-sm">Cancel</button>
          <button
            type="button"
            data-testid="markpaid-save"
            disabled={saving}
            onClick={save}
            className="bg-emerald-600 text-white font-semibold px-4 py-1.5 rounded-md hover:bg-emerald-700 disabled:opacity-50 text-sm flex items-center gap-1.5">
            <Check size={13}/> {saving ? "Recording…" : "Confirm & record"}
          </button>
        </div>
      </div>
    </div>
  );
}
